<template>
  <layout title="API Access">
    <div class="flex justify-center">
      <div class="md:p-8 p-4 !pb-28 max-w-7xl w-full flex items-stretch">
        <sideMenu page="apiaccess" />
        <div class="flex flex-col flex-1 md:ml-8 ml-4">
          <div class="font-black text-3xl text-gray-800 inline-block lg:col-span-2 col-span-1">
            API Access
          </div>
          <div
            class="mt-6 bg-white shadow-allArround rounded-xl flex-1 relative"
            v-if="loading || apikey"
          >
            <PuSkeleton class="h-full w-full z-10 rounded-xl absolute" v-if="loading" />
            <div class="p-4 bg-gray-200 font-bold text-xl rounded-t-xl">Your API Key</div>
            <div class="p-4 text-gray-800">
              <div class="flex flex-col">
                <div>
                  <p>
                    An API Key is similar to a password, and should be protected as such. Do not
                    share API Keys, store them in source code, or send them over insecure channels.
                  </p>
                  <p>Reset the API Key if you think an has been compromised.</p>
                </div>
                <div class="bg-gray-200 my-4 h-0.5"></div>
                <div class="flex flex-col items-start">
                  <p class="font-bold">API Key</p>
                  <div
                    class="flex mt-4"
                    :style="!apikey && 'height: 40px;max-width: 60ch;width: 100%;'"
                  >
                    <div
                      class="px-3 py-2 text-sm border-2 border-gray-200 break-all flex-1"
                      ref="apikeyinput"
                    >
                      {{ showKey ? apikey : apikeyCircles }}
                    </div>
                    <button
                      class="border-t-2 border-r-2 border-b-2 p-1 border-gray-200 flex items-center relative"
                      :title="showKey ? 'Hide API Key' : 'Show API Key'"
                      v-on:click="createMaskKey"
                    >
                      <div
                        class="absolute -top-7 -left-2 bg-gray-800 text-white px-1 text-sm rounded-md"
                        v-if="showCopy"
                      >
                        copied
                      </div>

                      <div>
                        <EyeOffIcon v-if="showKey" />
                        <EyeIcon v-else />
                      </div>
                    </button>
                  </div>
                  <div class="mt-6" v-if="clientId">
                    <div class="flex flex-col items-start">
                      <p class="font-bold">Client ID</p>
                      <div
                        class="flex mt-4"
                        :style="clientId == '' && 'height: 40px;max-width: 15ch;width: 100%;'"
                      >
                        <div
                          class="px-3 py-2 text-sm border-2 border-gray-200 break-all flex-1"
                          ref="clientIdinput"
                        >
                          {{ clientId }}
                        </div>
                        <div
                          class="border-t-2 border-r-2 border-b-2 p-1 border-gray-200 flex items-center relative"
                          title="Copy to clipboard"
                          @click="copyClientId"
                        >
                          <div
                            class="absolute -top-7 -left-2 bg-gray-800 text-white px-1 text-sm rounded-md"
                            v-if="showCopy2"
                          >
                            copied
                          </div>
                          <ClipboardCopyIcon />
                        </div>
                      </div>
                    </div>
                  </div>
                  <button class="mt-6 btn-danger" @click="toggleModal">Reset</button>
                  <modal
                    type="danger"
                    title="Reset API Key"
                    subtitle="Are you sure you want to reset?"
                    buttonTitle="Reset"
                    :show="showModal"
                    @closeModal="toggleModal"
                    @actionClick="reset"
                  >
                    <template>
                      <form @submit.prevent="reset" class="flex w-full items-center mt-5 flex-col">
                        <div class="text-left">
                          <input
                            id="user_password"
                            type="password"
                            name="user_password"
                            v-model="form.user_password"
                            required
                            class="w-60"
                            placeholder="Enter your password"
                          />
                          <div class="error" v-if="errors.user_password">
                            {{ errors.user_password }}
                          </div>
                        </div>
                      </form>
                    </template>
                  </modal>
                </div>
              </div>
            </div>
          </div>

          <div
            class="mt-6 bg-white shadow-allArround rounded-xl flex-1 relative"
            v-if="product != null && product.private_name.toLowerCase() == this.$products.KYC"
          >
            <div class="p-4 bg-gray-200 font-bold text-xl rounded-t-xl">
              Identity Verification Settings
            </div>
            <div class="p-4 text-gray-800">
              <div class="flex flex-col">
                <div>
                  <p>
                    Please fill in the configuration fields below for setting up your identity
                    verification flow
                  </p>
                </div>
                <div class="bg-gray-200 my-4 h-0.5"></div>

                <div class="max-w-xs">
                  <form role="form" @submit.prevent="updateKYCSettings" method="POST">
                    <div>
                      <label
                        for="allow_origins"
                        class="flex gap-1 text-sm font-medium leading-5 text-gray-700"
                        data-tooltip-target="tooltip-default"
                      >
                        Allow Origins
                        <tooltip
                          ><template #text
                            ><InformationCircleIcon class="text-primary-color w-5 h-5" /></template
                          ><template #tooltip
                            >If empty all origins will be allowed</template
                          ></tooltip
                        >
                      </label>
                      <div class="mt-1 rounded-md shadow-sm">
                        <vue-tags-input
                          placeholder="Add url"
                          v-model="url"
                          :tags="kyc.allow_origins"
                          @tags-changed="(newTags) => (kyc.allow_origins = newTags)"
                          :validation="[
                            {
                              classes: 'avoid-item',
                              rule: /http:\/\/(localhost|127\.0\.0\.1)(\/.*)?|https:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/,
                              disableAdd: true,
                            },
                          ]"
                        />
                      </div>
                      <div class="error" v-if="errors.allow_origins">
                        {{ errors.allow_origins }}
                      </div>
                    </div>

                    <div class="mt-6">
                      <label
                        for="webhook_url"
                        class="block text-sm font-medium leading-5 text-gray-700"
                        :class="{ required: !isManualVerification }"
                      >
                        Webhook Url
                      </label>
                      <div class="mt-1 rounded-md shadow-sm">
                        <input
                          id="webhook_url"
                          type="text"
                          name="webhook_url"
                          v-model="kyc.webhook_url"
                          class="w-full"
                          placeholder="https://example.com"
                          :required="!isManualVerification"
                        />
                      </div>
                      <div>
                        <p class="mt-2">
                          <a class="link text-sm" @click="toogleShowTestWebhookModal"
                            >Test Webhook</a
                          >
                        </p>
                      </div>
                      <div class="error" v-if="errors.webhook_url">
                        {{ errors.webhook_url }}
                      </div>
                    </div>

                    <div v-if="isManualVerification">
                      <div class="mt-6">
                        <label
                          for="email"
                          class="flex gap-1 text-sm font-medium leading-5 text-gray-700"
                          :class="{ required: isManualVerification }"
                        >
                          Webhook Email
                          <tooltip
                            ><template #text
                              ><InformationCircleIcon
                                class="text-primary-color w-5 h-5" /></template
                            ><template #tooltip
                              >E-mail address where the identity verification results will be
                              sent</template
                            ></tooltip
                          >
                        </label>
                        <div class="mt-1 rounded-md shadow-sm">
                          <input
                            id="email"
                            type="text"
                            name="email"
                            :required="isManualVerification"
                            class="w-full"
                            v-model="kyc.webhook_email"
                            placeholder="Webhook email"
                          />
                        </div>
                        <div class="error" v-if="errors.webhook_email">
                          {{ errors.webhook_email }}
                        </div>
                      </div>
                      <div class="mt-6">
                        <label
                          for="company_name"
                          class="flex gap-1 text-sm font-medium leading-5 text-gray-700"
                          :class="{ required: isManualVerification }"
                        >
                          Company Name
                        </label>
                        <div class="mt-1 rounded-md shadow-sm">
                          <input
                            id="company_name"
                            type="text"
                            name="company_name"
                            :required="isManualVerification"
                            class="w-full"
                            v-model="company_name"
                          />
                        </div>
                      </div>

                      <div class="mt-6">
                        <label
                          for="expire_date"
                          class="block text-sm font-medium leading-5 text-gray-700 required"
                        >
                          Token expiration time
                          <tooltip
                            ><template #text
                              ><InformationCircleIcon
                                class="text-primary-color w-5 h-5" /></template
                            ><template #tooltip
                              >The link that is sent to the user to perform identity verification
                              will expire after this time</template
                            ></tooltip
                          >
                        </label>
                        <div class="mt-1 rounded-md shadow-sm">
                          <multiselect
                            v-model="userSettings.periodSelected"
                            track-by="name"
                            label="name"
                            class="md:py-0 py-2"
                            :options="userSettings.periods"
                            :searchable="false"
                            :allow-empty="false"
                          >
                          </multiselect>
                        </div>
                      </div>
                    </div>

                    <button type="submit" class="btn-primary mt-6">Save Changes</button>
                  </form>
                </div>
              </div>
            </div>
          </div>

          <div
            class="mt-6 bg-white shadow-allArround rounded-xl flex-1 relative"
            v-if="
              product != null && product.private_name.toLowerCase() == 'kyc' && isManualVerification
            "
          >
            <div class="p-4 bg-gray-200 font-bold text-xl rounded-t-xl">
              Send Identity Verification Link
            </div>
            <div class="p-4 text-gray-800">
              <div class="flex flex-col">
                <div>
                  <p>
                    Send a link to the user’s e-mail address so he can perform the identity
                    verification process
                  </p>
                </div>
                <div class="bg-gray-200 my-4 h-0.5"></div>
                <div class="max-w-xs">
                  <form role="form" @submit.prevent="sendIdentityVerificationEmail" method="POST">
                    <div>
                      <label
                        for="email"
                        class="flex gap-1 text-sm font-medium leading-5 text-gray-700 required"
                      >
                        User e-mail address
                      </label>
                      <div class="mt-1 rounded-md shadow-sm">
                        <input
                          id="email"
                          type="text"
                          name="email"
                          required
                          class="w-full"
                          v-model="userSettings.email"
                        />
                      </div>
                    </div>

                    <div class="mt-6">
                      <label
                        for="user_id"
                        class="block text-sm font-medium leading-5 text-gray-700 required"
                      >
                        User Id
                      </label>
                      <div class="mt-1 rounded-md shadow-sm">
                        <input
                          id="user_id"
                          type="text"
                          name="user_id"
                          required
                          class="w-full"
                          v-model="userSettings.userId"
                        />
                      </div>
                    </div>

                    <button type="submit" class="btn-primary mt-6">Send Email</button>
                  </form>
                </div>
              </div>
            </div>
          </div>

          <div
            class="mt-6 bg-white shadow-allArround rounded-xl flex-1 relative"
            v-if="loadingProduct || product != null"
          >
            <PuSkeleton class="h-full w-full z-10 rounded-xl absolute" v-if="loadingProduct" />
            <div class="p-4 bg-gray-200 font-bold text-xl rounded-t-xl">Documentation</div>
            <div class="p-4 text-gray-800">
              <p v-if="product != null">
                To integrate the subscribed API please follow the link{{
                  product.api_base_url != null ? "s" : ""
                }}
                below for usage examples and documentation:
              </p>
              <div class="pt-2">
                <p class="mt-2" v-if="documentation">
                  Documentation:
                  <b>
                    <a :href="documentation" target="_blank" rel="noreferrer" class="link">
                      {{ documentation }}
                    </a>
                  </b>
                </p>
                <p class="mt-2" v-if="product != null && product.api_base_url != null">
                  Base endpoint: <b>{{ product.api_base_url }}</b>
                </p>
                <p class="mt-2">
                  Integration SDKs:
                  <b>
                    <a
                      href="https://github.com/dev-yoonik"
                      target="_blank"
                      rel="noreferrer"
                      class="link"
                      >https://github.com/dev-yoonik</a
                    >
                  </b>
                </p>
                <p
                  class="mt-2"
                  v-if="
                    product != null && product.private_name.toLowerCase() == this.$products.FACE
                  "
                >
                  Web demo:
                  <b
                    ><inertia-link :href="$route('face-demo')" class="link">{{
                      $route("face-demo")
                    }}</inertia-link>
                  </b>
                </p>
                <p
                  class="mt-2"
                  v-if="product != null && product.private_name.toLowerCase() == 'kyc'"
                >
                  Web demo:
                  <b
                    ><inertia-link :href="$route('kyc-demo')" class="link">{{
                      $route("kyc-demo")
                    }}</inertia-link>
                  </b>
                </p>

                <p
                  class="mt-2"
                  v-if="
                    product != null && product.private_name.toLowerCase() == this.$products.FACE
                  "
                >
                  Liveness detection:
                  <b
                    ><inertia-link :href="$route('liveness-detection')" class="link"
                      >{{ $route("liveness-detection") }}
                    </inertia-link>
                  </b>
                </p>
              </div>

              <p class="mt-4">
                For more information please
                <inertia-link :href="$route('company') + '#contactus'" class="link"
                  >contact us</inertia-link
                >
                or join us at our
                <a
                  href="https://discord.gg/SqHVQUFNtN"
                  target="_blank"
                  rel="noreferrer"
                  class="link"
                  >discord community</a
                >.
              </p>
            </div>
          </div>

          <div
            class="mt-6 bg-white shadow-allArround rounded-xl flex-1 relative p-4 flex justify-center items-center flex-col text-center"
            v-if="!loading && !apikey"
          >
            <div>Subscribe a plan to have your API Documentation.</div>
            <inertia-link :href="$route('pricing') + '#products'" class="link mt-2"
              >Go to Plans</inertia-link
            >
          </div>
        </div>
      </div>
      <modal
        ref="modal"
        title="Do you want to test webhook?"
        type="info"
        subtitle="This will send a POST request to test the webhook integration. This operation may take a few moments to complete."
        buttonTitle="Send request"
        :show="showTestWebhookModal"
        icon="info-circle"
        @closeModal="toogleShowTestWebhookModal"
        @actionClick="testWebhook"
      />

      <LoaderSpinner :show="testWebhookLoading" />
    </div>
  </layout>
</template>

<script>
const layout = () => import(/* webpackChunkName: "layout" */ "../../Components/Layout/layout.vue");
const sideMenu = () =>
  import(/* webpackChunkName: "sideMenu" */ "../../Components/Layout/User/sideMenu.vue");
const ClipboardCopyIcon = () =>
  import(/* webpackChunkName: "ClipboardCopyIcon" */ "@vue-hero-icons/outline").then(
    (Import) => Import.ClipboardCopyIcon
  );

const modal = () =>
  import(/* webpackChunkName: "sideMenu" */ "../../Components/modalConfirmMessage.vue");

const LoaderSpinner = () =>
  import(/* webpackChunkName: "sideMenu" */ "../../Components/loaderSpinner.vue");

import tooltip from "../../Components/tooltip.vue";
import { InformationCircleIcon, EyeIcon, EyeOffIcon } from "@vue-hero-icons/outline";

import VueTagsInput from "@johmun/vue-tags-input";
export default {
  components: {
    layout,
    sideMenu,
    ClipboardCopyIcon,
    modal,
    VueTagsInput,
    tooltip,
    InformationCircleIcon,
    EyeIcon,
    EyeOffIcon,
    LoaderSpinner,
  },
  props: {
    errors: Object,
  },
  data() {
    return {
      showCopy: false,
      showKey: false,
      showCopy2: false,
      apikey: null,
      apikeyCircles: "",
      showModal: false,
      loading: true,
      loadingProduct: true,
      product: null,
      clientId: null,
      kyc: {
        allow_origins: [],
        webhook_url: null,
        webhook_email: null,
      },
      company_name: null,
      url: "",
      isManualVerification: this.$page.props.auth.user.is_manual_verification,
      userSettings: {
        email: "",
        userId: "",
        periodSelected: { hours: 1, name: "30 minutes" },
        periods: [
          { hours: 1, name: "30 minutes" },
          { hours: 24, name: "1 day" },
          { hours: 168, name: "1 week" },
        ],
      },
      expire_time: null,
      showTestWebhookModal: false,
      testWebhookLoading: false,
      form: {
        user_password: "",
      },
      documentation: null,
    };
  },
  mounted() {
    this.getApiKey();
    this.getProduct();
    this.getKYCOptions();
  },
  methods: {
    copyAPIkey() {
      if (this.showKey) {
        window.getSelection().selectAllChildren(this.$refs.apikeyinput);
        navigator.clipboard.writeText(this.apikey);
        this.showCopy = true;

        let self = this;

        setTimeout(function () {
          self.showCopy = false;
        }, 3000);
      }
    },
    copyClientId() {
      window.getSelection().selectAllChildren(this.$refs.clientIdinput);
      navigator.clipboard.writeText(this.clientId);
      this.showCopy2 = true;
      let self = this;
      setTimeout(function () {
        self.showCopy2 = false;
      }, 3000);
    },
    toggleModal() {
      this.errors.user_password = "";
      this.showModal = !this.showModal;
    },
    reset() {
      const formUser = {
        user_password: this.form.user_password,
      };

      this.$inertia.post("/user/api/resetapikey", formUser, {
        preserveScroll: true,
        onSuccess: (e) => {
          if (e.props.flash.message) {
            if (e.props.flash.message_type === "success") {
              this.toggleModal();
              this.getApiKey();
              this.$store.commit("showFlashMessage", {
                message: e.props.flash.message,
                type: e.props.flash.message_type,
              });
            } else {
              this.$store.commit("showFlashMessage", {
                message: e.props.flash.message,
                type: e.props.flash.message_type,
              });
            }
            this.form.user_password = "";
          }
        },
      });
    },
    getProduct() {
      this.axios
        .get("/user/api/product")
        .then((response) => {
          if (response.data.name) {
            this.product = response.data;
            this.documentation = this.getUrlDocs(response.data);
          }
          this.loadingProduct = false;
        })
        .catch(() => {
          this.loadingProduct = false;
        });
    },
    getApiKey() {
      this.axios
        .get("/user/api/apikey")
        .then((response) => {
          if (response.data) {
            this.apikey = response.data.key;
            this.maskApiKey();
          }
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },

    maskApiKey() {
      let apiLength = this.apikey.length;
      this.apikeyCircles = "•".repeat(apiLength);
    },

    createMaskKey() {
      this.showKey = !this.showKey;
      this.copyAPIkey();
    },

    getKYCOptions() {
      this.axios
        .get("/user/api/kyc-options")
        .then((response) => {
          if (response.data) {
            this.clientId = response.data.client_id;
            this.kyc.webhook_url = response.data.webhook_url;
            this.kyc.webhook_email = response.data.webhook_email;
            this.company_name = response.data.company_name;

            let period = this.userSettings.periods.find(
              (e) => e.hours == response.data.expire_time
            );

            this.userSettings.periodSelected = period ? period : this.userSettings.periodSelected;
            if (response.data.allow_origins != "") {
              this.kyc.allow_origins = response.data.allow_origins.split(";");
              this.kyc.allow_origins.forEach((element, i) => {
                this.kyc.allow_origins[i] = { text: element };
              });
            }
          }
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    updateKYCSettings() {
      let urls = "";

      for (let i = 0; i < this.kyc.allow_origins.length; i++) {
        if (i === this.kyc.allow_origins.length - 1) {
          urls += this.kyc.allow_origins[i].text;
        } else {
          urls += this.kyc.allow_origins[i].text + ";";
        }
      }

      this.$inertia.post(
        "/user/api/kyc-options",
        {
          allow_origins: urls,
          webhook_url: this.kyc.webhook_url,
          webhook_email: this.kyc.webhook_email,
          expire_time: this.userSettings.periodSelected,
          company_name: this.company_name,
        },
        {
          preserveScroll: true,
          onSuccess: (e) => {
            if (e.props.flash.message != null)
              this.$store.commit("showFlashMessage", {
                message: e.props.flash.message,
                type: e.props.flash.message_type,
              });
          },
        }
      );
    },
    sendIdentityVerificationEmail() {
      this.axios
        .post("/user/api/identity-verification-email", {
          email: this.userSettings.email,
          userId: this.userSettings.userId,
          expiration: this.userSettings.periodSelected,
        })
        .then((response) => {
          if (response.data) {
            this.$store.commit("showFlashMessage", {
              message: response.data.message,
              type: response.data.message_type,
            });
          }
        })
        .catch((e) => {
          this.$store.commit("showFlashMessage", {
            message: e,
            type: "danger",
          });
        });
    },

    toogleShowTestWebhookModal() {
      this.showTestWebhookModal = !this.showTestWebhookModal;
    },

    testWebhook() {
      this.testWebhookLoading = true;
      this.axios
        .post("/test-webhook", {
          link: this.kyc.webhook_url,
        })
        .then((response) => {
          this.testWebhookLoading = false;
          if (response.data) {
            this.$store.commit("showFlashMessage", {
              message: response.data.message,
              type: response.data.message_type,
            });
          }
        })
        .catch((e) => {
          this.testWebhookLoading = false;
          this.$store.commit("showFlashMessage", {
            message: e,
            type: "danger",
          });
        });
    },

    getUrlDocs(product = this.product) {
      const privateName = product?.private_name?.toLowerCase();

      const docsByProduct = {
        face: "https://docs.youverse.id/docs/3.x/face",
        web: "https://docs.youverse.id/docs/1.x/authentication",
        kyc: "https://docs.youverse.id/docs/1.x/id_verification",
        youage: "https://docs.youverse.id/docs/1.x/age",
      };

      return privateName ? docsByProduct[privateName] || null : null;
    },
  },
};
</script>
